import { useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode } from "react";
import { useMutation } from "@tanstack/react-query";
import { Loader2, RefreshCw } from "lucide-react";

import {
  applyZplPrintSettings,
  DEFAULT_ZPL_MEDIA_DARKNESS_DELTA,
  DEFAULT_ZPL_PRINT_SPEED_IPS,
  extractZplPrintSettings,
  normalizeZplPrintSettings,
  type ZplPrintSettings,
} from "@integrator-src/api/zpl-print-settings";
import { apiFetch } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input, Label } from "@/components/ui/Input";
import { useHubToast } from "@/components/ui/HubToast";
import { AssemblySkuCombobox } from "@/components/mrp/AssemblySkuCombobox";
import type { MrpSkuMasterRow } from "@/lib/mrp";
import { cn } from "@/lib/utils";
import { LabelTemplateLayoutEditor } from "@/pages/labelTemplates/LabelTemplateLayoutEditor";
import {
  LabelPreviewRotationSelect,
  LabelPreviewWithRulers,
  type LabelPreviewRotationDeg,
} from "@/pages/labelTemplates/LabelPreviewWithRulers";
import {
  defaultHubBoxPreviewSku,
  formatLabelTemplateSampleData,
  parseLabelSizeCaption,
  parseLabelTemplateSampleDataText,
  parseZplLabelSizeInches,
} from "@/pages/labelTemplates/labelTemplatePreviewUtils";

interface PreviewResponse {
  zpl: string;
  png_base64: string;
  sample_data: Record<string, unknown>;
  preview_dpi?: number;
}

type BuilderPreviewRequest = {
  template: string;
  label_type: string | null;
  product_family: string;
  sample_data?: Record<string, unknown>;
  printer?: string;
  version?: number;
  design_dpi?: number;
  fillSampleText?: boolean;
  captureAsDefault?: boolean;
};

export interface LabelTemplatePreviewDraft {
  product_family: string;
  name: string;
  label_type: string;
  template: string;
}

interface LabelTemplatePreviewRow {
  id: number;
  version: number;
  readonly: boolean;
  product_family: string;
  label_type: string | null;
  useHardcodedLayoutVersion: boolean;
  label_size?: string | null;
  design_dpi?: number | null;
  speed_ips?: number | null;
  darkness_delta?: number | null;
  template?: string;
}

function resolveTemplatePrintSettings(
  row: LabelTemplatePreviewRow | null,
  draftTemplate: string,
): ZplPrintSettings {
  const fromDraft = extractZplPrintSettings(draftTemplate);
  const fromRow = row?.template != null ? extractZplPrintSettings(row.template) : {};
  return normalizeZplPrintSettings({
    speedIps:
      fromDraft.speedIps ??
      fromRow.speedIps ??
      row?.speed_ips ??
      DEFAULT_ZPL_PRINT_SPEED_IPS,
    darknessDelta:
      fromDraft.darknessDelta ??
      fromRow.darknessDelta ??
      row?.darkness_delta ??
      DEFAULT_ZPL_MEDIA_DARKNESS_DELTA,
  });
}

function previewImageUrl(pngBase64: string): string {
  return `data:image/png;base64,${pngBase64}`;
}

function FieldLabelWithReset(props: {
  htmlFor: string;
  label: string;
  resetTitle: string;
  onReset: () => void;
  disabled?: boolean;
}): ReactElement {
  return (
    <div className="flex items-center gap-1.5">
      <Label htmlFor={props.htmlFor}>{props.label}</Label>
      <button
        type="button"
        className="inline-flex rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
        title={props.resetTitle}
        aria-label={props.resetTitle}
        disabled={props.disabled}
        onClick={props.onReset}
      >
        <RefreshCw className="h-3.5 w-3.5" aria-hidden />
      </button>
    </div>
  );
}

export function LabelTemplatePreviewEditor(props: {
  apiBasePath: string;
  activeDraft: LabelTemplatePreviewDraft;
  selectedRow: LabelTemplatePreviewRow | null;
  creating: boolean;
  selectedId: number | null;
  selectedZplPrinter: string;
  onTestPrintContextChange?: (input: {
    zpl: string;
    speed_ips: number;
    darkness_delta: number;
  } | null) => void;
  detailsPanel?: ReactNode;
  onTemplateChange?: (template: string) => void;
  saveGitPending?: boolean;
  copyPending?: boolean;
  onSaveGitTemplate?: (
    template: string,
    printSettings: { speed_ips: number; darkness_delta: number },
  ) => void;
  onCopyTemplateToSupabase?: (template: string) => void;
}): ReactElement {
  const { showToast } = useHubToast();
  const [hubBoxTab, setHubBoxTab] = useState<"preview" | "zpl">("preview");
  const [sampleSku, setSampleSku] = useState("");
  const [sampleDataText, setSampleDataText] = useState("");
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [editableZpl, setEditableZpl] = useState("");
  const initialPrintSettings = resolveTemplatePrintSettings(
    props.selectedRow,
    props.activeDraft.template,
  );
  const [speedIps, setSpeedIps] = useState(initialPrintSettings.speedIps);
  const [darknessDelta, setDarknessDelta] = useState(initialPrintSettings.darknessDelta);
  const [speedInput, setSpeedInput] = useState(String(initialPrintSettings.speedIps));
  const [darknessInput, setDarknessInput] = useState(String(initialPrintSettings.darknessDelta));
  const [defaultSampleDataText, setDefaultSampleDataText] = useState("");
  const [layoutSourceZpl, setLayoutSourceZpl] = useState<string | null>(null);
  const [viewRotationDeg, setViewRotationDeg] = useState<LabelPreviewRotationDeg>(0);
  const autoPreviewKeyRef = useRef<string | null>(null);
  const selectedTemplateKey = props.creating
    ? "new"
    : props.selectedRow != null
      ? `${props.selectedRow.label_type ?? ""}:${props.selectedRow.product_family}:${String(props.selectedRow.version)}`
      : String(props.selectedId ?? "");
  const lastTemplateKeyRef = useRef<string | null>(null);
  const printSettingsRef = useRef<ZplPrintSettings>(initialPrintSettings);

  const printSettings = useMemo(
    () => normalizeZplPrintSettings({ speedIps, darknessDelta }),
    [speedIps, darknessDelta],
  );
  printSettingsRef.current = printSettings;

  if (lastTemplateKeyRef.current !== selectedTemplateKey) {
    lastTemplateKeyRef.current = selectedTemplateKey;
    printSettingsRef.current = resolveTemplatePrintSettings(
      props.selectedRow,
      props.activeDraft.template,
    );
  }

  function zplWithPrintSettings(zpl: string): string {
    if (zpl.trim() === "") return zpl;
    return applyZplPrintSettings(zpl, printSettingsRef.current);
  }

  const previewScopeKey = `${selectedTemplateKey}|${props.selectedZplPrinter}`;

  useEffect(() => {
    const nextSettings = resolveTemplatePrintSettings(
      props.selectedRow,
      props.activeDraft.template,
    );
    printSettingsRef.current = nextSettings;
    setSpeedIps(nextSettings.speedIps);
    setDarknessDelta(nextSettings.darknessDelta);
    setSpeedInput(String(nextSettings.speedIps));
    setDarknessInput(String(nextSettings.darknessDelta));
    setSampleSku("");
    setSampleDataText("");
    setPreview(null);
    setEditableZpl("");
    setDefaultSampleDataText("");
    setHubBoxTab("preview");
    setViewRotationDeg(0);
    setLayoutSourceZpl(null);
  }, [selectedTemplateKey]);

  useEffect(() => {
    autoPreviewKeyRef.current = null;
  }, [previewScopeKey]);

  useEffect(() => {
    const extracted = extractZplPrintSettings(props.activeDraft.template);
    if (
      extracted.speedIps !== undefined &&
      extracted.speedIps !== printSettingsRef.current.speedIps
    ) {
      setSpeedIps(extracted.speedIps);
      setSpeedInput(String(extracted.speedIps));
      printSettingsRef.current = { ...printSettingsRef.current, speedIps: extracted.speedIps };
    }
    if (
      extracted.darknessDelta !== undefined &&
      extracted.darknessDelta !== printSettingsRef.current.darknessDelta
    ) {
      setDarknessDelta(extracted.darknessDelta);
      setDarknessInput(String(extracted.darknessDelta));
      printSettingsRef.current = {
        ...printSettingsRef.current,
        darknessDelta: extracted.darknessDelta,
      };
    }
  }, [props.activeDraft.template]);

  function handleSpeedInputChange(rawValue: string): void {
    setSpeedInput(rawValue);
    const trimmed = rawValue.trim();
    if (trimmed === "" || !Number.isFinite(Number(trimmed))) return;
    const next = normalizeZplPrintSettings({
      speedIps: Number(trimmed),
      darknessDelta: printSettingsRef.current.darknessDelta,
    });
    setSpeedIps(next.speedIps);
    printSettingsRef.current = next;
    setEditableZpl((current) =>
      current.trim() === "" ? current : applyZplPrintSettings(current, next),
    );
    if (!props.selectedRow?.readonly) {
      props.onTemplateChange?.(applyZplPrintSettings(props.activeDraft.template, next));
    }
  }

  function handleDarknessInputChange(rawValue: string): void {
    setDarknessInput(rawValue);
    const trimmed = rawValue.trim();
    if (trimmed === "" || trimmed === "-" || !Number.isFinite(Number(trimmed))) return;
    const next = normalizeZplPrintSettings({
      speedIps: printSettingsRef.current.speedIps,
      darknessDelta: Number(trimmed),
    });
    setDarknessDelta(next.darknessDelta);
    printSettingsRef.current = next;
    setEditableZpl((current) =>
      current.trim() === "" ? current : applyZplPrintSettings(current, next),
    );
    if (!props.selectedRow?.readonly) {
      props.onTemplateChange?.(applyZplPrintSettings(props.activeDraft.template, next));
    }
  }

  const builderPreviewMutation = useMutation({
    mutationFn: (input: BuilderPreviewRequest) => {
      const { fillSampleText: _fillSampleText, captureAsDefault: _captureAsDefault, ...body } = input;
      return apiFetch<PreviewResponse>(`${props.apiBasePath}/render-preview`, {
        method: "POST",
        body,
      });
    },
    onSuccess: (data, variables) => {
      setPreview(data);
      setEditableZpl(applyZplPrintSettings(data.zpl, printSettingsRef.current));
      const sampleJson = formatLabelTemplateSampleData(data.sample_data);
      if (variables.captureAsDefault === true) {
        setDefaultSampleDataText(sampleJson);
      }
      if (variables.fillSampleText === true) {
        setSampleDataText(sampleJson);
      }
    },
    onError: (error) => {
      setPreview(null);
      showToast({ tone: "error", message: (error as Error).message });
    },
  });

  const sampleDataForSkuMutation = useMutation({
    mutationFn: (input: { sku: string; deviceSerial?: string }) =>
      apiFetch<{ sample_data: Record<string, unknown> }>(`${props.apiBasePath}/sample-data-for-sku`, {
        method: "POST",
        body: {
          sku: input.sku,
          product_family: props.activeDraft.product_family.trim(),
          label_type: props.activeDraft.label_type.trim() === "" ? null : props.activeDraft.label_type.trim(),
          ...(input.deviceSerial != null && input.deviceSerial.trim() !== ""
            ? { device_serial: input.deviceSerial.trim() }
            : {}),
        },
      }),
    onError: (error) => {
      showToast({ tone: "error", message: (error as Error).message });
    },
  });

  const zplPreviewMutation = useMutation({
    mutationFn: (zpl: string) =>
      apiFetch<PreviewResponse>(`${props.apiBasePath}/render-preview`, {
        method: "POST",
        body: {
          zpl,
          ...(props.selectedZplPrinter !== "" ? { printer: props.selectedZplPrinter } : {}),
          ...(props.selectedRow?.design_dpi != null ? { design_dpi: props.selectedRow.design_dpi } : {}),
          ...(props.selectedRow?.label_type != null ? { label_type: props.selectedRow.label_type } : {}),
          ...(props.selectedRow != null ? { product_family: props.selectedRow.product_family } : {}),
          ...(props.selectedRow != null && props.selectedRow.version > 0
            ? { version: props.selectedRow.version }
            : {}),
        },
      }),
    onSuccess: (data) => {
      setPreview(data);
    },
    onError: (error) => {
      showToast({ tone: "error", message: (error as Error).message });
    },
  });

  const previewBusy =
    builderPreviewMutation.isPending || zplPreviewMutation.isPending || sampleDataForSkuMutation.isPending;

  function buildBuilderPreviewRequest(options?: {
    sampleData?: Record<string, unknown>;
    useDefaultSample?: boolean;
    fillSampleText?: boolean;
    captureAsDefault?: boolean;
  }): BuilderPreviewRequest | null {
    const request: BuilderPreviewRequest = {
      template: props.activeDraft.template,
      label_type: props.activeDraft.label_type.trim() === "" ? null : props.activeDraft.label_type.trim(),
      product_family: props.activeDraft.product_family.trim(),
      fillSampleText: options?.fillSampleText,
      captureAsDefault: options?.captureAsDefault,
    };
    if (props.selectedZplPrinter !== "") {
      request.printer = props.selectedZplPrinter;
    }
    if (props.selectedRow != null && props.selectedRow.version > 0) {
      request.version = props.selectedRow.version;
    }
    if (props.selectedRow?.design_dpi != null) {
      request.design_dpi = props.selectedRow.design_dpi;
    }
    if (options?.useDefaultSample !== true && options?.sampleData != null) {
      request.sample_data = options.sampleData;
    }
    return request;
  }

  function runBuilderPreview(options?: {
    sampleData?: Record<string, unknown>;
    useDefaultSample?: boolean;
    fillSampleText?: boolean;
    captureAsDefault?: boolean;
  }): void {
    const request = buildBuilderPreviewRequest(options);
    if (request != null) builderPreviewMutation.mutate(request);
  }

  function applySampleData(sampleData: Record<string, unknown>, options?: { updateSkuField?: boolean }): void {
    const sampleJson = formatLabelTemplateSampleData(sampleData);
    setSampleDataText(sampleJson);
    if (options?.updateSkuField === true) {
      const skuField = sampleData.sku;
      if (typeof skuField === "string" && skuField.trim() !== "") {
        setSampleSku(skuField.trim());
      }
    }
    runBuilderPreview({ sampleData, fillSampleText: false });
  }

  function applySampleJsonFromText(): void {
    try {
      const sampleData = parseLabelTemplateSampleDataText(sampleDataText);
      runBuilderPreview({ sampleData, fillSampleText: false });
    } catch (error) {
      showToast({ tone: "error", message: (error as Error).message });
    }
  }

  function regenerateZplFromSampleJson(): void {
    applySampleJsonFromText();
  }

  async function reloadSampleJsonFromSku(): Promise<void> {
    const trimmed = sampleSku.trim();
    if (trimmed === "") {
      showToast({ tone: "error", message: "Enter a sample SKU to reload JSON." });
      return;
    }
    await loadSampleFromSku(trimmed);
  }

  function clearSampleSkuAndResetToDefault(): void {
    setSampleSku("");
    if (defaultSampleDataText.trim() === "") {
      runBuilderPreview({ useDefaultSample: true, fillSampleText: true, captureAsDefault: true });
      return;
    }
    setSampleDataText(defaultSampleDataText);
    try {
      const sampleData = parseLabelTemplateSampleDataText(defaultSampleDataText);
      runBuilderPreview({ sampleData, fillSampleText: false });
    } catch {
      runBuilderPreview({ useDefaultSample: true, fillSampleText: true, captureAsDefault: true });
    }
  }

  async function loadSampleFromSku(sku: string): Promise<void> {
    const trimmed = sku.trim();
    if (trimmed === "") return;
    try {
      let deviceSerial: string | undefined;
      try {
        const current = parseLabelTemplateSampleDataText(sampleDataText);
        if (typeof current.serial === "string" && current.serial.trim() !== "") {
          deviceSerial = current.serial.trim();
        }
      } catch {
        // keep default serial on API
      }
      const result = await sampleDataForSkuMutation.mutateAsync({ sku: trimmed, deviceSerial });
      setSampleSku(trimmed);
      applySampleData(result.sample_data, { updateSkuField: true });
    } catch {
      if (sampleDataText.trim() === "") {
        runBuilderPreview({ useDefaultSample: true, fillSampleText: true, captureAsDefault: true });
      }
    }
  }

  function handleSkuPick(row: MrpSkuMasterRow): void {
    void loadSampleFromSku(row.sku);
  }

  function runZplPreview(zpl: string): void {
    if (zpl.trim() === "") return;
    zplPreviewMutation.mutate(zpl.trim());
  }

  useEffect(() => {
    if (autoPreviewKeyRef.current === previewScopeKey) return;
    autoPreviewKeyRef.current = previewScopeKey;
    const liveSku =
      props.activeDraft.label_type.trim() === "hub-box"
        ? defaultHubBoxPreviewSku(props.activeDraft.product_family)
        : null;
    if (liveSku != null) {
      void loadSampleFromSku(liveSku);
      return;
    }
    runBuilderPreview({ useDefaultSample: true, fillSampleText: true, captureAsDefault: true });
    // Initial default sample + ZPL when the template or printer changes.
  }, [previewScopeKey, props.activeDraft.template]);

  const previewImageSrc = useMemo(
    () => (preview?.png_base64 != null ? previewImageUrl(preview.png_base64) : null),
    [preview?.png_base64],
  );

  const previewRulerSize = useMemo(() => {
    const dpi = props.selectedRow?.design_dpi ?? preview?.preview_dpi ?? 300;
    return (
      parseZplLabelSizeInches(preview?.zpl ?? editableZpl, dpi) ??
      parseLabelSizeCaption(props.selectedRow?.label_size)
    );
  }, [editableZpl, preview?.preview_dpi, preview?.zpl, props.selectedRow?.design_dpi, props.selectedRow?.label_size]);

  useEffect(() => {
    setEditableZpl((current) => (current.trim() === "" ? current : applyZplPrintSettings(current, printSettings)));
  }, [printSettings]);

  useEffect(() => {
    props.onTestPrintContextChange?.({
      zpl: editableZpl.trim(),
      speed_ips: printSettings.speedIps,
      darkness_delta: printSettings.darknessDelta,
    });
  }, [editableZpl, printSettings, props.onTestPrintContextChange]);

  useEffect(() => {
    return () => {
      props.onTestPrintContextChange?.(null);
    };
  }, [props.onTestPrintContextChange]);

  const previewWorkspace = (
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(280px,26rem)]">
        <div className="flex min-h-[24rem] flex-col gap-4">
          <div>
            <Label htmlFor="template-sample-sku">Sample SKU</Label>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <AssemblySkuCombobox
                id="template-sample-sku"
                value={sampleSku}
                onChange={setSampleSku}
                placeholder="Search catalog SKU to fill sample data…"
                className="min-w-[16rem] flex-1"
                disabled={previewBusy}
                enforceCatalogSku
                onPickMasterRow={handleSkuPick}
                onBlurCommittedWithSku={(sku) => {
                  if (sku !== "") void loadSampleFromSku(sku);
                }}
              />
              <Button
                type="button"
                size="sm"
                variant="secondary"
                isLoading={sampleDataForSkuMutation.isPending}
                disabled={sampleSku.trim() === "" || previewBusy}
                onClick={() => void loadSampleFromSku(sampleSku)}
              >
                Load SKU
              </Button>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={previewBusy}
                onClick={() => clearSampleSkuAndResetToDefault()}
              >
                Clear
              </Button>
            </div>
            <p className="mt-1 text-xs text-slate-500">
              Load SKU fills JSON from the catalog. Clear removes the SKU and restores template default JSON.
            </p>
          </div>

          <div className="flex min-h-0 flex-1 flex-col">
            <FieldLabelWithReset
              htmlFor="template-sample-data"
              label="Sample data (JSON object)"
              resetTitle="Reload sample JSON from SKU, then rebuild ZPL and preview"
              disabled={previewBusy}
              onReset={() => void reloadSampleJsonFromSku()}
            />
            <textarea
              id="template-sample-data"
              value={sampleDataText}
              onChange={(event) => setSampleDataText(event.target.value)}
              spellCheck={false}
              placeholder="Edit sample JSON manually, or use the refresh icon beside Rendered ZPL to rebuild ZPL from your edits."
              className="mt-1 min-h-[10rem] flex-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 font-mono text-xs text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </div>

        </div>

        <div className="flex min-h-[24rem] flex-col">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs leading-5 text-slate-400">
              {preview?.preview_dpi != null
                ? props.selectedRow?.design_dpi != null && preview.preview_dpi === props.selectedRow.design_dpi
                  ? `Previewed at ${String(preview.preview_dpi)} dpi native${
                      props.selectedRow.label_size?.trim() ? ` (${props.selectedRow.label_size} stock)` : ""
                    }`
                  : preview.preview_dpi === 203
                    ? "Previewed at 203 dpi (no printer scaling)"
                    : `Previewed at ${String(preview.preview_dpi)} dpi for ${props.selectedZplPrinter || "the selected printer"}`
                : "\u00a0"}
            </p>
            <LabelPreviewRotationSelect
              id="label-preview-view-rotation"
              value={viewRotationDeg}
              onChange={setViewRotationDeg}
            />
          </div>
          <div
            aria-live="polite"
            className="mt-1 flex min-h-0 flex-1 items-center justify-center overflow-auto rounded-lg border border-slate-200 bg-white p-3"
          >
            {previewBusy ? (
              <div className="flex flex-col items-center gap-2 text-sm text-slate-500">
                <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
                Rendering preview…
              </div>
            ) : previewImageSrc != null ? (
              previewRulerSize != null ? (
                <LabelPreviewWithRulers
                  src={previewImageSrc}
                  alt="Rendered label preview"
                  widthInches={previewRulerSize.widthInches}
                  heightInches={previewRulerSize.heightInches}
                  rotationDeg={viewRotationDeg}
                />
              ) : (
                <img
                  src={previewImageSrc}
                  alt="Rendered label preview"
                  className="max-h-[32rem] max-w-full border border-dashed border-slate-400 object-contain"
                  style={{ transform: `rotate(${String(viewRotationDeg)}deg)` }}
                />
              )
            ) : (
              <p className="max-w-xs text-center text-sm text-slate-500">
                Load a SKU or regenerate ZPL to render the label image.
              </p>
            )}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              isLoading={zplPreviewMutation.isPending}
              disabled={editableZpl.trim() === ""}
              onClick={() => runZplPreview(zplWithPrintSettings(editableZpl))}
            >
              Refresh preview
            </Button>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={editableZpl.trim() === ""}
              onClick={() => setLayoutSourceZpl(editableZpl)}
            >
              Adjust size & position
            </Button>
          </div>
        </div>
      </div>
  );

  if (layoutSourceZpl != null) {
    return (
      <LabelTemplateLayoutEditor
        sourceZpl={layoutSourceZpl}
        sourceTemplate={props.activeDraft.template}
        previewImageSrc={previewImageSrc}
        previewRulerSize={previewRulerSize}
        viewRotationDeg={viewRotationDeg}
        onViewRotationDegChange={setViewRotationDeg}
        previewBusy={previewBusy}
        onPreviewPatchedZpl={(zpl) => runZplPreview(zplWithPrintSettings(zpl))}
        onClose={(result) => {
          setLayoutSourceZpl(null);
          if (result == null) return;
          setEditableZpl(zplWithPrintSettings(result.zpl));
          props.onTemplateChange?.(result.template);
          if (result.zpl.trim() !== "") runZplPreview(zplWithPrintSettings(result.zpl));
        }}
        saveGitPending={props.saveGitPending}
        copyPending={props.copyPending}
        onSaveGit={
          props.onSaveGitTemplate == null
            ? undefined
            : (result) => {
                const templateWithSettings = applyZplPrintSettings(result.template, printSettings);
                setEditableZpl(zplWithPrintSettings(result.zpl));
                props.onTemplateChange?.(templateWithSettings);
                props.onSaveGitTemplate?.(templateWithSettings, {
                  speed_ips: printSettings.speedIps,
                  darkness_delta: printSettings.darknessDelta,
                });
                setLayoutSourceZpl(null);
                if (result.zpl.trim() !== "") runZplPreview(zplWithPrintSettings(result.zpl));
              }
        }
        onCopySupabase={
          props.onCopyTemplateToSupabase == null
            ? undefined
            : (result) => {
                const templateWithSettings = applyZplPrintSettings(result.template, printSettings);
                setEditableZpl(zplWithPrintSettings(result.zpl));
                props.onTemplateChange?.(templateWithSettings);
                props.onCopyTemplateToSupabase?.(templateWithSettings);
              }
        }
      />
    );
  }

  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-2 border-b border-slate-200 pb-px">
        <div className="flex flex-wrap gap-1" role="tablist" aria-label="Label template panels">
        <button
          type="button"
          role="tab"
          id="label-template-tab-preview"
          aria-selected={hubBoxTab === "preview"}
          aria-controls="label-template-panel-preview"
          className={cn(
            "rounded-t-md px-3 py-2 text-sm font-medium transition-colors",
            hubBoxTab === "preview"
              ? "relative z-10 border border-b-0 border-slate-200 bg-white text-slate-900 shadow-sm"
              : "border border-transparent bg-slate-50 text-slate-600 hover:bg-slate-100",
          )}
          onClick={() => setHubBoxTab("preview")}
        >
          Preview
        </button>
        <button
          type="button"
          role="tab"
          id="label-template-tab-zpl"
          aria-selected={hubBoxTab === "zpl"}
          aria-controls="label-template-panel-zpl"
          className={cn(
            "rounded-t-md px-3 py-2 text-sm font-medium transition-colors",
            hubBoxTab === "zpl"
              ? "relative z-10 border border-b-0 border-slate-200 bg-white text-slate-900 shadow-sm"
              : "border border-transparent bg-slate-50 text-slate-600 hover:bg-slate-100",
          )}
          onClick={() => setHubBoxTab("zpl")}
        >
          ZPL & details
        </button>
        </div>
        {props.onSaveGitTemplate != null || props.onCopyTemplateToSupabase != null ? (
          <div className="mb-1 flex flex-wrap gap-2">
            {props.onSaveGitTemplate != null ? (
              <Button
                type="button"
                size="sm"
                isLoading={props.saveGitPending}
                onClick={() => {
                  const templateWithSettings = applyZplPrintSettings(
                    props.activeDraft.template,
                    printSettings,
                  );
                  props.onTemplateChange?.(templateWithSettings);
                  props.onSaveGitTemplate?.(templateWithSettings, {
                    speed_ips: printSettings.speedIps,
                    darkness_delta: printSettings.darknessDelta,
                  });
                }}
              >
                Save Git template
              </Button>
            ) : null}
            {props.onCopyTemplateToSupabase != null ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                isLoading={props.copyPending}
                onClick={() => {
                  const templateWithSettings = applyZplPrintSettings(
                    props.activeDraft.template,
                    printSettings,
                  );
                  props.onTemplateChange?.(templateWithSettings);
                  props.onCopyTemplateToSupabase?.(templateWithSettings);
                }}
              >
                Copy to Supabase
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      <div
        id="label-template-panel-preview"
        role="tabpanel"
        aria-labelledby="label-template-tab-preview"
        hidden={hubBoxTab !== "preview"}
        className="space-y-4 pt-4"
      >
        {previewWorkspace}
      </div>

      <div
        id="label-template-panel-zpl"
        role="tabpanel"
        aria-labelledby="label-template-tab-zpl"
        hidden={hubBoxTab !== "zpl"}
        className="space-y-5 pt-4"
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor="template-print-speed">Print speed (ips)</Label>
            <Input
              id="template-print-speed"
              type="number"
              min={1}
              max={12}
              step={1}
              value={speedInput}
              onChange={(event) => handleSpeedInputChange(event.target.value)}
              onBlur={() => setSpeedInput(String(printSettings.speedIps))}
              className="mt-1"
            />
            <p className="mt-1 text-xs text-slate-500">Zebra ^PR. Default 2 ips.</p>
          </div>
          <div>
            <Label htmlFor="template-print-darkness">Darkness (Δ)</Label>
            <Input
              id="template-print-darkness"
              type="number"
              min={-30}
              max={30}
              step={1}
              value={darknessInput}
              onChange={(event) => handleDarknessInputChange(event.target.value)}
              onBlur={() => setDarknessInput(String(printSettings.darknessDelta))}
              className="mt-1"
            />
            <p className="mt-1 text-xs text-slate-500">Zebra ^MD relative darkness. Default −8.</p>
          </div>
        </div>

        <div className="flex min-h-0 flex-col">
          <FieldLabelWithReset
            htmlFor="template-rendered-zpl"
            label="Rendered ZPL"
            resetTitle="Rebuild ZPL from current sample JSON and refresh preview"
            disabled={previewBusy}
            onReset={() => regenerateZplFromSampleJson()}
          />
          <textarea
            id="template-rendered-zpl"
            value={editableZpl}
            onChange={(event) => setEditableZpl(event.target.value)}
            spellCheck={false}
            placeholder="Rendered ZPL output. Edit to experiment, or use the refresh icon to rebuild from the template + sample JSON."
            className="mt-1 min-h-[12rem] w-full rounded-md border border-slate-300 bg-white px-3 py-2 font-mono text-xs text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </div>

        {props.detailsPanel}

        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            isLoading={zplPreviewMutation.isPending}
            disabled={editableZpl.trim() === ""}
            onClick={() => runZplPreview(zplWithPrintSettings(editableZpl))}
          >
            Refresh preview
          </Button>
        </div>
      </div>
    </>
  );
}
